// axios-клиент для backend API.
//
// Архитектура:
//   - single-origin через Vite proxy → cookies/CSRF работают без cross-origin настроек.
//   - axios самостоятельно читает csrftoken cookie и шлёт его в X-CSRFToken
//     для всех не-GET запросов (xsrfCookieName / xsrfHeaderName).
//   - Response interceptor конвертирует ошибку в NormalizedError и поднимает
//     событие "api:error" — глобальный слой UI (в Layout) показывает toast.
//   - 401 на любом endpoint, кроме /auth/me/, поднимает событие "api:unauthorized" —
//     ProtectedRoute редиректит на /login с возвратом.
//   - 401 на /auth/me/ — НЕ редиректит (это нормальный сигнал «не залогинен»).
import axios from "axios";
import { normalizeError, type NormalizedError } from "./errors";
import { API_TIMEOUT_MS } from "./constants";

// Расширение конфига axios необязательным полем — обратносовместимо: вызов без
// него даёт ровно прежнее поведение (BP-010, dual generation §8.1).
declare module "axios" {
  interface AxiosRequestConfig {
    /** Новое, необязательное. true → ошибка запроса НЕ поднимает глобальный тост
     *  (событие api:error). 401 по-прежнему уводит на /login. По умолчанию — прежнее поведение. */
    silentErrors?: boolean;
  }
}

/** Точный путь /me/ — используется в interceptor для исключения этого endpoint
 *  из общего цикла «401 → /login». 401 на /me/ — нормальная проверка сессии. */
const ME_PATH = "/api/auth/me/";

export const apiClient = axios.create({
  baseURL: "/",
  withCredentials: true,
  xsrfCookieName: "csrftoken",
  xsrfHeaderName: "X-CSRFToken",
  timeout: API_TIMEOUT_MS,
  headers: {
    "X-Requested-With": "XMLHttpRequest",
  },
});

// Custom event'ы — взаимодействие с UI без жёсткой завязки на React.
// Любой компонент может подписаться через addEventListener.
export const API_EVENTS = {
  unauthorized: "multitool:api:unauthorized",
  error: "multitool:api:error",
} as const;

export interface APIErrorEventDetail {
  url: string | undefined;
  method: string | undefined;
  error: NormalizedError;
}

function emitError(detail: APIErrorEventDetail): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(API_EVENTS.error, { detail }));
  }
}

function emitUnauthorized(returnTo: string): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(API_EVENTS.unauthorized, { detail: { returnTo } }),
    );
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const normalized = normalizeError(error);
    const config = error?.config as
      | { url?: string; method?: string; silentErrors?: boolean }
      | undefined;
    const url = config?.url;
    const method = config?.method?.toUpperCase();
    // Тихий режим (BP-010 §8.1): вызывающий код сам показывает сообщение об
    // ошибке (обычно — ровно один тост вместо двух). 401 тихий режим не
    // затрагивает — редирект на /login должен сработать всегда.
    const silent = Boolean(config?.silentErrors);

    // 401 → редирект на /login, кроме самой /auth/me/ (она используется
    // для проверки сессии и должна корректно возвращать «не залогинен»).
    // Используем точное сравнение (не substring), чтобы '/api/auth/me/something'
    // не попал в исключение.
    if (normalized.status === 401 && url && !urlMatchesMe(url)) {
      const path =
        typeof window !== "undefined"
          ? window.location.pathname + window.location.search
          : "/";
      emitUnauthorized(path);
    } else {
      // Все остальные ошибки — единый канал для toast'ов в UI.
      // Не дублируем 401 (страница /login сама покажет ошибку через mutation).
      if (normalized.status !== 401 && !silent) {
        emitError({ url, method, error: normalized });
      }
    }

    // Прокидываем normalized ошибку дальше — компоненты могут читать её через
    // err.cause / поле `__normalized` (см. addNormalizedError ниже).
    type AugmentedError = typeof error & { normalized?: NormalizedError };
    (error as AugmentedError).normalized = normalized;
    return Promise.reject(error);
  },
);

/** Проверка url на принадлежность к /api/auth/me/.
 *  Парсит url через URL API (с произвольным base для относительных путей) и
 *  сравнивает pathname точно. Это надёжнее endsWith — отсекает запросы вида
 *  /api/auth/me/admin/users/, где endsWith('/me/') ложно совпало бы только
 *  если бы такой путь существовал (его нет, но контракт лучше явный). */
function urlMatchesMe(url: string): boolean {
  try {
    // 'http://placeholder' как base — игнорируется если url абсолютный.
    return new URL(url, "http://placeholder").pathname === ME_PATH;
  } catch {
    // Невалидный URL — фолбэк на строгое равенство относительного пути.
    return url === ME_PATH;
  }
}

/** Достать NormalizedError из axios-ошибки, не делая повторную нормализацию. */
export function getNormalized(err: unknown): NormalizedError {
  if (err && typeof err === "object" && "normalized" in err) {
    const n = (err as { normalized?: NormalizedError }).normalized;
    if (n) return n;
  }
  return normalizeError(err);
}
